import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';
import { IComparisonPopulationSetting } from '../../../providers/settings/comparisonPopulationSettingsProvider';
import { IDateRange } from '../../../providers/settings/dates/dateRangeProvider';
import { IProfileReportSettings } from '../../../providers/settings/reports/profileReportSettingsProvider';
import { ISettingsTemplate } from '../../../providers/settings/reports/types';
import { userAsPromise } from '../../../providers/userProvider';
import { ISelectOption } from '../../../types';

/**
 * @description updates the profile template
 * @param template
 * @param isSelected
 */
export const updateProfileTemplate = async (
  template: ISettingsTemplate<IProfileReportSettings>,
  isSelected: boolean
): Promise<void> => {
  const user = await userAsPromise();

  if (isSelected) {
    const batch = writeBatch(user.services.firestore);
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/profileReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/profileReport`
      ),
      { selected: template },
      { merge: true }
    );
    await batch.commit();
  } else {
    await setDoc(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/profileReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
  }
};

/**
 * @description creates a new profile report settings template
 * @param template
 */
export const createProfileTemplate = async (
  template: Partial<ISettingsTemplate<IProfileReportSettings>>
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    collection(
      user.services.firestore,
      `userProfiles/${user.id}/settings/profileReport/templates`
    )
  );
  const newDocument = {
    ...template,
    id: docRef.id,
    active: true,
  };
  await setDoc(docRef, newDocument, { merge: true });
};

/**
 * @description saves the template as the selected template and
 * @param template
 */
export const saveProfileSettings = async (
  template: ISettingsTemplate<IProfileReportSettings>,
  targetTimeframe: IDateRange,
  graph: ISelectOption<string>,
  comparisonPopulation: IComparisonPopulationSetting
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/profileReport`
  );

  await setDoc(
    docRef,
    {
      selected: template ?? null,
      targetTimeframe: targetTimeframe ?? null,
      graph,
      comparisonPopulation,
    },
    { merge: true }
  );
};

/**
 * @description delete the template and change the current template to another one
 * @param template
 * @param replacement
 * @returns
 */
export const deleteSelectedTemplate = async (
  template: ISettingsTemplate<IProfileReportSettings>,
  replacement: ISettingsTemplate<IProfileReportSettings>
): Promise<void> => {
  const user = await userAsPromise();

  const batch = writeBatch(user.services.firestore);
  const updateRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/profileReport/templates/${template.id}`
  );
  const update: ISettingsTemplate<IProfileReportSettings> = {
    ...template,
    active: false,
  };
  batch.update(updateRef, update as unknown as Record<string, unknown>);
  const selectedRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/profileReport`
  );
  batch.set(selectedRef, { selected: replacement ?? null }, { merge: true });
  return batch.commit();
};
